import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Clock,
  Sparkles,
  RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { getChapter, getNeighbours, CHAPTERS } from "@/tutorial/chapters";
import {
  isChapterCompleted,
  setChapterCompleted,
} from "@/tutorial/progress";
import { TutorialPDFExportButton } from "@/tutorial/components/TutorialPDFExporter";

/**
 * Shared shell used by every chapter placeholder (Chapter0 → Chapter10).
 *
 * Renders:
 *  - hero (chapter number, title, duration, completed badge)
 *  - placeholder zone for real content (filled in T-3 / T-4)
 *  - footer nav (prev/next + "Marquer comme complété")
 */
export default function ChapterShell({ id, children }) {
  const routeParams = useParams();
  const navigate = useNavigate();
  const chapterId = id !== undefined ? id : Number(routeParams.id);
  const chapter = getChapter(chapterId);
  const [done, setDone] = useState(() => isChapterCompleted(chapterId));

  useEffect(() => {
    setDone(isChapterCompleted(chapterId));
  }, [chapterId]);

  if (!chapter) {
    return (
      <div className="py-10 text-center text-muted-foreground" data-testid="chapter-not-found">
        Chapitre introuvable.{" "}
        <Link to="/tutorial" className="underline">
          Retour au sommaire
        </Link>
      </div>
    );
  }

  const { prev, next } = getNeighbours(chapterId);
  const num = String(chapter.id).padStart(2, "0");

  const toggleDone = () => {
    const nextVal = !done;
    setDone(nextVal);
    setChapterCompleted(chapter.id, nextVal);
    toast[nextVal ? "success" : "info"](
      nextVal
        ? `Chapitre ${num} marqué comme complété.`
        : `Chapitre ${num} remis à non lu.`
    );
  };

  return (
    <article
      data-testid={`tutorial-chapter-${chapter.id}`}
      className="space-y-8"
    >
      <header>
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5 text-brand-gold" />
          <span>Chapitre {num}</span>
          <span>·</span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {chapter.duration} min
          </span>
          {done && (
            <span
              className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300"
              data-testid={`chapter-${chapter.id}-done-badge`}
            >
              <CheckCircle2 className="h-3 w-3" />
              Complété
            </span>
          )}
        </div>
        <h1
          className="mt-2 font-judicial text-3xl font-black leading-tight tracking-tight sm:text-4xl"
          data-testid={`chapter-${chapter.id}-title`}
        >
          {chapter.title}
        </h1>
      </header>

      <section
        className="space-y-10"
        data-testid={`chapter-${chapter.id}-content`}
      >
        {children || (
          <div
            className="rounded-lg border border-dashed border-border/70 bg-muted/30 p-8 text-center text-sm text-muted-foreground"
            data-testid={`chapter-${chapter.id}-placeholder`}
          >
            [Contenu à venir — Prompt T-3 et T-4]
          </div>
        )}
      </section>

      <div
        className="mt-2 flex justify-center border-t border-border pt-6"
        data-testid={`chapter-${chapter.id}-pdf-cta`}
      >
        <TutorialPDFExportButton
          testId={`chapter-${chapter.id}-pdf-export-btn`}
          label="Télécharger le tutoriel complet (PDF)"
        />
      </div>

      <footer
        className="flex flex-col gap-4 border-t border-border pt-6 sm:flex-row sm:items-center sm:justify-between"
        data-testid={`chapter-${chapter.id}-footer`}
      >
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!prev}
            onClick={() =>
              prev && navigate(`/tutorial/chapter/${prev.id}`)
            }
            data-testid={`chapter-${chapter.id}-prev-btn`}
          >
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            {prev ? `Chapitre ${String(prev.id).padStart(2, "0")}` : "Précédent"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!next}
            onClick={() =>
              next && navigate(`/tutorial/chapter/${next.id}`)
            }
            data-testid={`chapter-${chapter.id}-next-btn`}
          >
            {next ? `Chapitre ${String(next.id).padStart(2, "0")}` : "Suivant"}
            <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>

        <Button
          size="sm"
          variant={done ? "secondary" : "default"}
          onClick={toggleDone}
          aria-label={
            done
              ? `Marquer le chapitre ${num} (${chapter.title}) comme non lu`
              : `Marquer le chapitre ${num} (${chapter.title}) comme complété`
          }
          data-testid={`chapter-${chapter.id}-mark-done-btn`}
        >
          {done ? (
            <>
              <RotateCcw className="mr-1.5 h-4 w-4" />
              Marquer comme non lu
            </>
          ) : (
            <>
              <CheckCircle2 className="mr-1.5 h-4 w-4" />
              Marquer comme complété
            </>
          )}
        </Button>
      </footer>

      {/* Surface the current position in the series for screen-readers */}
      <div className="sr-only">
        Chapitre {chapter.id + 1} sur {CHAPTERS.length}.
      </div>
    </article>
  );
}
